import type { Account, ProductType } from '@shared/products-types';
import type { EmptyResponse, UpdateByIdArg } from '@luna/api';
import { createEntity, request } from '@luna/api';
import { getEnv } from '@luna/env';
import type { RetailEnvironmentConfig } from '@retail/core-config';
import type { Amount } from '@shared/core-types';
import { getProductType } from '@shared/products-core';
import { isFeatureEnabled } from '@luna/gatekeeper';
import { logger } from '@luna/logger';
import { logout } from '@shared/auth';

type GAPIQueryAllParam = {
    accountsFilter?: (accounts: Account[]) => Account[];
    onSuccess?: (accounts: Account[]) => void;
};
type QueryByIdParam = {
    id: string;
    onSuccess?: (account: Account) => void;
};
type DeleteByIdParam = {
    id: string;
    onSuccess?: () => void;
};

type UpdateByIdParam = {
    onSuccess?: (response?: unknown) => void;
} & UpdateByIdArg<Account>;

const filterOutLegacyAccounts = (accounts: Account[]): Account[] =>
    accounts.filter((account: Account) => !account.flags?.includes('hr-legacyCurrencyAccount'));

export const gAPIAccountEntitySlice = createEntity({
    name: 'products/accounts/gAPIAccount',
    withEntityType: {} as Account,
    conditionQueryById: (account) => !!account?.id,
    payloadCreators: {
        queryAll: async ({ accountsFilter, onSuccess }: GAPIQueryAllParam = {}) => {
            const { collection = [] } =
                (await request<{ collection: Account[] }>(
                    `${getEnv().georgeApiUrl}my/accounts`,
                ).catch((err) => {
                    logger.error('ProductCollection :: The main George API Loading Failed ', err);
                    logout('outage');
                    throw err;
                })) ?? {};

            const isDropArchiveAccountsEnabled = isFeatureEnabled((f) => f.dropLegacyAccounts);

            const filteredAccounts: Account[] = accountsFilter?.(collection) ?? collection;

            const cleanedAccounts = isDropArchiveAccountsEnabled
                ? filterOutLegacyAccounts(filteredAccounts)
                : filteredAccounts;

            onSuccess?.(cleanedAccounts);

            return cleanedAccounts;
        },
        queryById: async ({ id, onSuccess }: QueryByIdParam) => {
            const gAPIAccount =
                (await request<Account>(`${getEnv().georgeApiUrl}my/accounts/${id}`)) ?? {};

            const isDropArchiveAccountsEnabled = isFeatureEnabled((f) => f.dropLegacyAccounts);

            if (
                isDropArchiveAccountsEnabled &&
                gAPIAccount.flags?.includes('hr-legacyCurrencyAccount')
            ) {
                throw new Error('Legacy archive account fetched through entity');
            }

            onSuccess?.(gAPIAccount);

            return gAPIAccount;
        },
        deleteById: async ({ id, onSuccess }: DeleteByIdParam) => {
            await request.delete<EmptyResponse>(
                `${getEnv<RetailEnvironmentConfig>().georgeApiUrl}my/accounts/${id}`,
            );

            onSuccess?.();

            return null;
        },
        updateById: async ({ id, changes, onSuccess }: UpdateByIdParam) => {
            const gAPIAccount = await request.put<Account>(
                `${getEnv<RetailEnvironmentConfig>().georgeApiUrl}my/accounts/${id}`,
                changes,
            );

            onSuccess?.(gAPIAccount);

            return gAPIAccount;
        },
        create: async (payload: Partial<Account>) =>
            request.post<Account>(
                `${getEnv<RetailEnvironmentConfig>().georgeApiUrl}my/accounts`,
                payload,
            ),
    },
});

export const {
    updateById: updateGAPIAccountById,
    selectAll: selectGAPIAccounts,
    selectById: selectGAPIAccountById,
    selectTotal: selectTotalGAPIAccounts,
    selectSlice: selectGAPISlice,
    useEntities: useGAPIAccounts,
    useEntity: useGAPIAccount,
    create: createGAPIAccount,
    deleteById: deleteGAPIAccount,
    queryById: fetchGAPIAccount,
} = gAPIAccountEntitySlice;

export const selectGAPIAccountBalance = (accountId: string): Amount | undefined => {
    return selectGAPIAccountById(accountId)?.balance;
};

export const selectGAPIAccount = (accountId: string): Account | undefined => {
    return selectGAPIAccountById(accountId);
};

export const selectGAPIAccountProductType = (accountId: string): ProductType | undefined => {
    const product = selectGAPIAccountById(accountId);
    if (!product) {
        return undefined;
    }

    return getProductType(product);
};
